import {
  CarouselType,
  CuratedAssetType,
  CuratedCategoryType,
  ListType,
  MenuItemType,
  MenuType,
  PageType,
  RecommendedType,
  SearchType,
  SmartListType,
  UrlType,
} from '@vimond/services/content-discovery';
import { ProductType } from '@vimond/services/content-discovery/_categories';
import { getEnvVar } from '@vimond/utils/env';
import { getProductIds } from './getProductIds';

export type Element =
  | ListType
  | CarouselType
  | SmartListType
  | PageType
  | MenuType
  | MenuItemType
  | CuratedAssetType
  | CuratedCategoryType
  | UrlType
  | RecommendedType
  | SearchType;

const isFreeNoLoginContent = (product: ProductType): boolean =>
  product &&
  typeof product.accessType === 'string' &&
  product.accessType.toUpperCase() === 'FREE_NOLOGIN';

const isFreeLoginContent = (product: ProductType): boolean =>
  product &&
  typeof product.accessType === 'string' &&
  product.accessType.toUpperCase() === 'FREE_LOGIN';

const hasMatchingProduct =
  (products: Set<string>) =>
  (product: ProductType): boolean =>
    product &&
    typeof product.id === 'string' &&
    product.id.length > 0 &&
    products.has(product.id);

export const hasAccess = (
  requiredProducts: ProductType[],
  products: Set<string>,
  isLoggedIn: boolean,
): boolean => {
  if (!Array.isArray(requiredProducts) || requiredProducts.length === 0) {
    // If no products are specified, content is unrestricted - allow access
    return true;
  }

  // Check if any product allows free no-login access
  if (requiredProducts.some(isFreeNoLoginContent)) {
    return true;
  }

  // Check if any product allows free login access and user is logged in
  if (isLoggedIn && requiredProducts.some(isFreeLoginContent)) {
    return true;
  }

  // Check if user has matching paid products
  return requiredProducts.some(hasMatchingProduct(products));
};

const filterByAccess = (
  element: ListType | CuratedAssetType | CuratedCategoryType,
  products: Set<string>,
  isLoggedIn: boolean,
): boolean => {
  if (element.contentType === 'asset' || element.contentType === 'category') {
    if (!element?.content) {
      return false;
    }

    const requiredProducts = element.content.products ?? [];
    return hasAccess(requiredProducts, products, isLoggedIn);
  }
  return true;
};

function filterList(
  res: ListType[],
  products: Set<string>,
  isLoggedIn: boolean,
) {
  return res
    .filter((p) => filterByAccess(p, products, isLoggedIn))
    .map((p) => filterElement(p, products, isLoggedIn))
    .filter((item) => !!item);
}

function filterElement(
  content: ListType | undefined,
  products: Set<string>,
  isLoggedIn: boolean,
): ListType | undefined {
  const children = content?.elements;
  if (children) {
    const elements =
      filterList(children as ListType[], products, isLoggedIn) ?? [];
    if (elements.length === 0) {
      return undefined;
    }
    return { ...content, elements } as ListType;
  }

  return content;
}

const isUserLoggedIn = (token: string): boolean => {
  return !!(token && typeof token === 'string' && token.trim().length > 0);
};

export const filterAccessibleContent = (
  content: ListType | undefined,
  token: string,
): ListType | undefined => {
  const showInaccessibleContent =
    getEnvVar('NEXT_PUBLIC_SHOW_INACCESSIBLE_CONTENT') === 'true';

  // If we should show all content regardless of access, return early
  if (showInaccessibleContent) {
    return content;
  }

  const products = getProductIds(token);
  const isLoggedIn = isUserLoggedIn(token);

  // Filter the content based on access rules
  return filterElement(content, products, isLoggedIn);
};
